import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/* ---------- Layout ---------- */
export const Section = ({ children, className = '', id, dataTestId, edgeLabel }) => (
  <section
    id={id}
    data-testid={dataTestId}
    className={`relative px-6 md:px-10 lg:px-12 ${className}`}
  >
    <div className="max-w-7xl mx-auto relative">
      {edgeLabel && (
        <div className="absolute right-0 -top-6 md:top-2 edge-number hidden md:block">
          {edgeLabel}
        </div>
      )}
      {children}
    </div>
  </section>
);

/* ---------- Eyebrow — small caps caption with rule ---------- */
export const Eyebrow = ({ children, className = '' }) => (
  <div className={`inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-amber-300/85 font-mono ${className}`}>
    <span className="inline-block w-7 h-px bg-amber-300/60" />
    <span>{children}</span>
  </div>
);

/* ---------- Fade in on scroll ---------- */
export const FadeIn = ({ children, delay = 0, y = 24, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- Masked line reveal — signature on-load moment ---------- */
export const MaskedLine = ({ children, delay = 0, className = '' }) => (
  <span className="mask-line">
    <span
      className={`mask-inner ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </span>
  </span>
);

/* ---------- Editorial marquee ---------- */
export const Marquee = ({ items, reverse = false, className = '' }) => {
  // Duplicate for seamless loop
  const doubled = [...items, ...items];
  return (
    <div
      className={`overflow-hidden ${className}`}
      data-testid="editorial-marquee"
      aria-hidden="true"
    >
      <div className={`marquee-track ${reverse ? 'reverse' : ''}`}>
        {doubled.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-6 px-6 font-display text-3xl md:text-5xl lg:text-6xl tracking-tight text-white/85"
          >
            <span>{item}</span>
            <span className="star-divider" />
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Chapter — numbered manifesto slab ---------- */
export const Chapter = ({ num, title, body, children }) => (
  <FadeIn>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-16 md:py-24 border-t border-white/[0.06] relative">
      <div className="lg:col-span-4">
        <div className="chapter-num">{num}</div>
      </div>
      <div className="lg:col-span-8">
        <h3 className="font-display font-extrabold tracking-tight text-3xl md:text-5xl text-white leading-[1.05] max-w-2xl">
          {title}
        </h3>
        {body && (
          <p className="mt-6 font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl">
            {body}
          </p>
        )}
        {children}
      </div>
    </div>
  </FadeIn>
);

/* ---------- Card with tracking spotlight ---------- */
export const SpotlightCard = ({ children, className = '', onMouseMove, ...rest }) => {
  const ref = useRef(null);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--y', `${e.clientY - rect.top}px`);
    if (onMouseMove) onMouseMove(e);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      className={`card-spot card-hover rounded-3xl border border-white/10 bg-white/[0.025] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
};

/* ---------- Parallax wrapper ---------- */
export const Parallax = ({ children, offset = 80, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <div ref={ref} className={className}>
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
};

/* ---------- Page hero for inner pages ---------- */
export const PageHero = ({ eyebrow, title, intro, accent }) => (
  <div className="pt-36 md:pt-44 pb-16 md:pb-24 px-6 md:px-10 lg:px-12 relative overflow-hidden" data-testid="page-hero">
    <div className="absolute inset-0 spotlight-hero pointer-events-none" />
    <div className="max-w-7xl mx-auto relative">
      <FadeIn>
        <Eyebrow>{eyebrow}</Eyebrow>
      </FadeIn>
      <FadeIn delay={0.08}>
        <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.03] max-w-4xl">
          {title}
          {accent && <span className="font-serif-i text-amber-300"> {accent}</span>}
        </h1>
      </FadeIn>
      {intro && (
        <FadeIn delay={0.16}>
          <p className="mt-8 max-w-2xl font-body text-base sm:text-lg text-white/65 leading-relaxed">
            {intro}
          </p>
        </FadeIn>
      )}
    </div>
  </div>
);
